import type { SelectableMetric } from "@/components/entity-column-selector";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { MetricDefinition } from "@/hooks/api/use-field-definitions";
import {
  DEFAULT_WEBPAGE_METRIC_ORDER,
  METRIC_DATA_TYPES,
  WEBPAGE_METRIC_CONFIG,
} from "@/pages/webpages/webpage-explorer/webpage-metrics-config";

/**
 * Extract webpage metrics in different formats for various use cases.
 * This function provides both UI-ready metrics and formula-ready metrics.
 */
export function extractWebpageMetrics(): {
  /** Metrics formatted for UI components like EntityColumnSelector */
  selectableMetrics: SelectableMetric[];
  /** Metrics formatted for formula system with MetricDefinition interface */
  formulaMetrics: MetricDefinition[];
} {
  const selectableMetrics: SelectableMetric[] =
    DEFAULT_WEBPAGE_METRIC_ORDER.map((metricKey) => {
      const metricConfig = WEBPAGE_METRIC_CONFIG[metricKey];
      const categoryStyle = CATEGORY_STYLES[metricConfig.category];

      return {
        id: metricKey,
        title: metricConfig.title,
        tooltip: metricConfig.tooltip,
        category: metricConfig.category,
        categoryStyle: categoryStyle.color,
        categoryIcon: categoryStyle.icon,
      };
    });

  const formulaMetrics: MetricDefinition[] = DEFAULT_WEBPAGE_METRIC_ORDER.map(
    (metricKey) => {
      const metricConfig = WEBPAGE_METRIC_CONFIG[metricKey];
      const dataType = METRIC_DATA_TYPES[metricKey];

      // Map metric data types to formula-compatible types
      const formulaDataType = mapMetricDataTypeToFormulaType(dataType);

      return {
        key: metricKey,
        displayName: metricConfig.title,
        dataType: formulaDataType,
        description: metricConfig.tooltip,
      };
    },
  );

  return {
    selectableMetrics,
    formulaMetrics,
  };
}

const typeMap: Record<string, string> = {
  integer: "number",
  float: "number",
  currency: "currency",
  date: "date",
  number: "number", // fallback
};

/**
 * Map metric data types to formula-compatible data types
 */
function mapMetricDataTypeToFormulaType(metricDataType: string): string {
  return typeMap[metricDataType] || "number"; // Default to number for unknown types
}
